import React from "react";
import Icon from "@icon-park/react/es/all";

import Container from "./styled/Container";
import TextArea from "./styled/TextArea";
import DisabledText from "./styled/DisabledText";
import ErrorContainer from "./styled/ErrorContainer";
import Text from "../Text";
import GenericInput from "../GenericInput";
import { theme } from "../../styles";
import Tooltip from "../Tooltip";
import { ColorType } from "../../styles/theme";

export type Props = {
  value: string;
  testId?: string;
  name?: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onFocus?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  errorMessage?: string;
  /** Has assistive technology announce the error message when it appears. */
  announceError?: boolean;
  placeholder?: string;
  heading?: string;
  type?: string;
  disabled?: boolean;
  isLong?: boolean;
  height?: string;
  disabledWithoutHiding?: boolean;
  style?: React.CSSProperties;
  tooltip?: {
    content: React.ReactNode;
    textColor?: ColorType;
    label?: string;
    labelFont?: string;
    labelColor?: ColorType;
    title?: string;
    titleColor?: ColorType;
    backgroundColor?: ColorType;
    icon?: string;
    iconColor?: ColorType;
    place?: "top" | "right" | "bottom" | "left";
  };
};

const Input: React.FC<Props> = ({
  placeholder,
  value,
  testId,
  name,
  onChange,
  onBlur,
  onFocus,
  disabled,
  isLong,
  errorMessage = "",
  announceError = false,
  type = "text",
  heading,
  height,
  disabledWithoutHiding = false,
  tooltip,
  ...props
}) => {
  const InputComponent: React.ElementType = isLong ? TextArea : GenericInput;

  return (
    <Container height={height}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          paddingBottom: "7px",
        }}
      >
        {heading && (
          <Text type="S16" color="SLATE800">
            {heading}
          </Text>
        )}
        {tooltip && (
          <Tooltip id={`${testId || name || "input"}-tooltip`} {...tooltip} />
        )}
      </div>
      {disabled ? (
        <DisabledText>{value}</DisabledText>
      ) : (
        <InputComponent
          data-testid={testId}
          type={type}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          value={value}
          hasError={errorMessage}
          isLong={isLong}
          name={name}
          disabled={disabledWithoutHiding}
          disabledWithoutHiding={disabledWithoutHiding}
          {...props}
        />
      )}
      {errorMessage && (
        <ErrorContainer role={announceError ? "alert" : undefined}>
          <Icon
            type="Caution"
            size={14}
            fill={theme.colors.ERROR800}
            theme="filled"
          />
          <Text type="P14" color="ERROR800">
            {errorMessage}
          </Text>
        </ErrorContainer>
      )}
    </Container>
  );
};

export default Input;
